'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { videoLibraryData } from '@/lib/videoData';

const sectionColors: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  'my-profile': { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-400' },
  'member-sign': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-400' },
  'member-list': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-400' },
  'group-meeting-admin': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-400' },
  'events': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', dot: 'bg-green-400' },
  'group-meeting-member': { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', dot: 'bg-teal-400' },
  'request-category': { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', dot: 'bg-yellow-400' },
};

export default function SectionAccordion() {
  const [openSections, setOpenSections] = useState<string[]>(['my-profile']);

  const toggle = (id: string) => {
    setOpenSections((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const expandAll = () => setOpenSections(videoLibraryData.map((s) => s.id));
  const collapseAll = () => setOpenSections([]);

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-700 text-foreground">Tutorial Sections</h2>
          <p className="text-sm text-muted-foreground mt-0.5">7 sections · 20 video tutorials</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={expandAll}
            className="text-xs font-500 text-primary hover:text-primary/80 px-3 py-1.5 rounded-lg hover:bg-accent transition-colors"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="text-xs font-500 text-muted-foreground hover:text-foreground px-3 py-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Sections */}
      {videoLibraryData.map((section, sIdx) => {
        const isOpen = openSections.includes(section.id);
        const colors = sectionColors[section.id] || sectionColors['my-profile'];

        return (
          <div
            key={`section-${section.id}`}
            id={section.id}
            className={`bg-card rounded-xl border ${colors.border} overflow-hidden section-card-hover`}
          >
            {/* Section header */}
            <button
              onClick={() => toggle(section.id)}
              className="w-full flex items-center justify-between px-5 py-4 text-left group"
              aria-expanded={isOpen}
            >
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon name={section.icon as Parameters<typeof Icon>[0]['name']} size={18} className={colors.text} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-700 text-foreground text-base">{section.label}</h3>
                    <span className={`badge-section ${colors.bg} ${colors.text}`}>
                      {section.topics.length} {section.topics.length === 1 ? 'tutorial' : 'tutorials'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{section.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <div className="hidden sm:flex items-center gap-1">
                  {section.topics.slice(0, 3).map((t) => (
                    <div key={`dot-${t.id}`} className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
                  ))}
                  {section.topics.length > 3 && (
                    <span className="text-xs text-muted-foreground ml-1">+{section.topics.length - 3}</span>
                  )}
                </div>
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isOpen ? colors.bg : 'bg-muted'}`}>
                  <Icon
                    name={isOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                    size={16}
                    className={isOpen ? colors.text : 'text-muted-foreground'}
                  />
                </div>
              </div>
            </button>

            {/* Topics list */}
            {isOpen && (
              <div className="border-t border-border">
                {/* Table header */}
                <div className="hidden sm:grid grid-cols-12 px-5 py-2 bg-muted/60 text-xs font-600 text-muted-foreground uppercase tracking-wide">
                  <div className="col-span-1">#</div>
                  <div className="col-span-5">Topic</div>
                  <div className="col-span-4">Video File</div>
                  <div className="col-span-2 text-right">Action</div>
                </div>

                {section.topics.map((topic, tIdx) => (
                  <div
                    key={`topic-${topic.id}`}
                    className="video-row-hover grid grid-cols-1 sm:grid-cols-12 items-center px-5 py-3.5 border-b border-border/60 last:border-0 gap-2 sm:gap-0"
                  >
                    {/* Index */}
                    <div className="hidden sm:flex col-span-1 items-center">
                      <span className="text-sm font-600 text-muted-foreground font-tabular w-5 text-center">
                        {tIdx + 1}
                      </span>
                    </div>

                    {/* Topic label */}
                    <div className="col-span-5 flex items-center gap-2.5">
                      <div className={`w-7 h-7 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon name="PlayIcon" size={12} className={colors.text} />
                      </div>
                      <div>
                        <p className="text-sm font-600 text-foreground">{topic.label}</p>
                        <p className="text-xs text-muted-foreground sm:hidden">{topic.filename}</p>
                      </div>
                    </div>

                    {/* Filename */}
                    <div className="hidden sm:flex col-span-4 items-center gap-1.5">
                      <Icon name="FilmIcon" size={14} className="text-muted-foreground flex-shrink-0" />
                      <span className="text-xs text-muted-foreground font-mono truncate">{topic.filename}</span>
                    </div>

                    {/* Actions */}
                    <div className="col-span-2 flex items-center justify-start sm:justify-end gap-2">
                      <Link
                        href={`/video-player?topic=${topic.id}&section=${section.id}`}
                        className="inline-flex items-center gap-1.5 btn-primary text-xs px-3 py-1.5"
                      >
                        <Icon name="PlayIcon" size={12} className="text-white" />
                        <span>Watch</span>
                      </Link>
                      <a
                        href={topic.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hidden sm:inline-flex items-center gap-1 btn-secondary text-xs px-2.5 py-1.5"
                        title="Open video link in new tab"
                      >
                        <Icon name="ArrowTopRightOnSquareIcon" size={12} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}